import React from 'react';
import type { Editor } from '@tiptap/react';

import { type CaraWorkspaceFieldsFragment, CaraWorkspaceState } from '~/api/graphql';
import { findBlankField } from '~/components/email-templates/blank-field-actions';
import { blankFieldReason, blankTemplateFields } from '~/components/email-templates/blank-fields';
import { showFilledFieldLabels } from '~/components/email-templates/draft-markers';
import { FEATURE_FLAGS, useFlag } from '~/components/feature-flags';
import type { FocusedArtifact } from '~/components/workspaces/lib/email-artifact-body';
import { newerTemplateThanDraft } from '~/components/workspaces/artifact-focus-email-older-version-notice';
import type { TemplatePick } from '~/components/workspaces/artifact-focus-email-template-picker';
import { useComposerEmailTemplates } from './use-composer-email-templates';
import {
  type EmailTemplateSession,
  type RedraftRequest,
  redraftMessage,
  WAIT_FOR_CARA_REASON
} from './use-template-redraft';

type BlankRange = NonNullable<ReturnType<typeof findBlankField>>;

/**
 * Everything the composer knows about the template an email was drafted from: its blanks,
 * Cara redrafting it, and a newer version. The composer owns the email; this owns the template.
 */
export const useComposerTemplateState = (params: {
  workspace: CaraWorkspaceFieldsFragment;
  artifact: FocusedArtifact;
  subject: string;
  session: EmailTemplateSession;
  /** Called as a redraft is asked for, so the composer takes Cara's new subject. */
  onRedraftAsked: () => void;
}) => {
  const { workspace, artifact, subject, session } = params;
  const email = artifact.email;
  const isFlagEnabled = useFlag(FEATURE_FLAGS.EMAIL_TEMPLATES);
  const isDraftedFromTemplate = isFlagEnabled && !!email?.template;
  const templates = useComposerEmailTemplates(email?.template?.templateId);
  const [isHighlighting, setIsHighlighting] = React.useState(true);
  const [bodyEditor, setBodyEditor] = React.useState<Editor | null>(null);
  React.useEffect(() => {
    if (bodyEditor && !bodyEditor.isDestroyed) showFilledFieldLabels(bodyEditor, isHighlighting);
  }, [bodyEditor, isHighlighting]);

  // The version the agent last typed in, so picking a template asks before replacing their
  // words but not words Cara wrote since. A version saved before this composer opened says so
  // with editedAt, which Cara's next version starts without.
  const editedInVersion = React.useRef<number | null>(null);
  // The body as the blank-field check reads it. State, unlike the composer's ref, so a blank
  // filled in re-arms Send.
  const [checkedBodyHtml, setCheckedBodyHtml] = React.useState(email?.bodyHtml ?? '');
  const recordBodyEdit = (bodyHtml: string) => {
    editedInVersion.current = artifact.version;
    if (isDraftedFromTemplate) setCheckedBodyHtml(bodyHtml);
  };
  const recordSubjectEdit = () => {
    editedInVersion.current = artifact.version;
  };
  const hasEditedDraft = () => editedInVersion.current === artifact.version || !!artifact.editedAt;

  const placeholderLabels = email?.template?.placeholderLabels;
  const blankFieldLabels = React.useMemo(
    () =>
      isDraftedFromTemplate && placeholderLabels
        ? blankTemplateFields(subject, checkedBodyHtml, placeholderLabels)
        : [],
    [isDraftedFromTemplate, placeholderLabels, subject, checkedBodyHtml]
  );
  const firstBodyBlankLabel = bodyEditor
    ? blankFieldLabels.find((label) => findBlankField(bodyEditor, label))
    : undefined;
  const actOnBlankField = (label: string, act: (editor: Editor, blank: BlankRange) => unknown) => {
    const blank = bodyEditor && findBlankField(bodyEditor, label);
    if (blank) act(bodyEditor, blank);
  };

  const { redraft } = session;
  const isCaraWriting = workspace.state === CaraWorkspaceState.Working || !!redraft.writingNotice;
  // Sending also waits for Cara's redraft and for every field to be filled. Saving to the
  // mailbox doesn't: a mailbox draft is somewhere to finish the email.
  const sendReason = redraft.writingNotice
    ? WAIT_FOR_CARA_REASON
    : blankFieldReason(blankFieldLabels);

  const askForRedraft = (request: RedraftRequest) => {
    params.onRedraftAsked();
    void redraft.ask({
      message: redraftMessage(subject, request),
      request,
      emailDraftArtifactId: artifact.id
    });
  };
  const pickTemplate = (pick: TemplatePick) =>
    askForRedraft(
      pick
        ? { kind: 'template', templateId: pick.id, templateName: pick.name }
        : { kind: 'withoutTemplate' }
    );

  const newerTemplate = newerTemplateThanDraft(
    templates.draftTemplate,
    email?.template?.templateVersion
  );
  const isOlderVersionNoticeShown =
    !!newerTemplate && session.keptTemplateVersion !== newerTemplate.version;
  const redraftWithLatestVersion = () => {
    if (newerTemplate) {
      askForRedraft({
        kind: 'latestVersion',
        templateId: newerTemplate.id,
        templateName: newerTemplate.name
      });
    }
  };

  return {
    isDraftedFromTemplate,
    templates,
    isHighlighting,
    setIsHighlighting,
    setBodyEditor,
    checkBody: setCheckedBodyHtml,
    recordBodyEdit,
    recordSubjectEdit,
    hasEditedDraft,
    blankFieldLabels,
    firstBodyBlankLabel,
    actOnBlankField,
    isCaraWriting,
    sendReason,
    redraft,
    pickTemplate,
    newerTemplate,
    isOlderVersionNoticeShown,
    keepOlderVersion: () => newerTemplate && session.keepTemplateVersion(newerTemplate.version),
    redraftWithLatestVersion
  };
};

export type ComposerTemplateState = ReturnType<typeof useComposerTemplateState>;
